import { Review, ProfileResponse } from '@/types/supabase';
import { platformApiFetch } from '@/services/platformApi/client';

export interface ReviewCreateData {
  provider_id: string;
  user_id: string;
  rating: number;
  comment?: string | null;
  contacted_provider: boolean;
}

export interface ReviewUpdateData {
  rating?: number;
  comment?: string | null;
  contacted_provider?: boolean;
}

/**
 * Create a new review
 */
export async function createReview(data: ReviewCreateData): Promise<void> {
  await platformApiFetch('/v1/reviews', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing review
 */
export async function updateReview(id: string, data: ReviewUpdateData): Promise<void> {
  await platformApiFetch(`/v1/reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a review
 */
export async function deleteReview(id: string): Promise<void> {
  await platformApiFetch(`/v1/reviews/${id}`, { method: 'DELETE' });
}

/**
 * Fetch reviews for a service provider
 */
export async function fetchProviderReviews(providerId: string) {
  const data = await platformApiFetch<Array<Review & { profiles: ProfileResponse | null }>>(`/v1/reviews/provider/${providerId}`);
  return data.map((review) => ({
    id: review.id,
    rating: review.rating,
    comment: review.comment,
    contacted_provider: review.contacted_provider,
    created_at: review.created_at,
    profiles: review.profiles
      ? {
          first_name: review.profiles.first_name,
          last_name: review.profiles.last_name,
        }
      : null,
  }));
}

/**
 * Fetch all public reviews grouped by provider
 */
export async function fetchAllPublicReviews(): Promise<Record<string, Array<{id: string, provider_id: string, rating: number, is_public: boolean}>>> {
  return platformApiFetch<Record<string, Array<{id: string, provider_id: string, rating: number, is_public: boolean}>>>('/v1/reviews/public-grouped');
}

/**
 * Fetch all reported reviews with user profiles
 */
export async function fetchReportedReviews(): Promise<Review[]> {
  return platformApiFetch<Review[]>('/v1/reviews/reported');
}

/**
 * Fetch reviews for a specific provider with admin filtering
 */
export async function fetchProviderReviewsForUser(providerId: string, isAdmin: boolean = false): Promise<Review[]> {
  const query = isAdmin ? '?admin=true' : '';
  return platformApiFetch<Review[]>(`/v1/reviews/provider/${providerId}${query}`);
}

/**
 * Verify review update by fetching review data
 */
export async function verifyReviewUpdate(reviewId: string): Promise<{ id: string; is_public: boolean; provider_id: string } | null> {
  return platformApiFetch<{ id: string; is_public: boolean; provider_id: string } | null>(`/v1/reviews/verify/${reviewId}`);
}

/**
 * Fetch reviews for admin moderation with optional filtering
 */
export async function fetchReviewsForModeration(filterReported: boolean = false): Promise<Review[]> {
  const query = filterReported ? '?reportedOnly=true' : '';
  return platformApiFetch<Review[]>(`/v1/reviews/moderation${query}`);
}
